import React from 'react';
import {
  Box,
  ButtonBase,
  Collapse,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { HistoryOutlined, FileDownloadOutlined, ExpandMore } from '@mui/icons-material';
import FeedList from './FeedList';
import MetricTooltip from './MetricTooltip';
import METRIC_DEFINITIONS from './metricDefinitions';

/** Mobile: compact preview below the main dashboard column. */
const MOBILE_INITIAL_SESSIONS = 2;
const MOBILE_SESSIONS_PER_PAGE = 2;
/** Desktop sidebar: enough cards to fill the sticky viewport rail without extra clicks. */
const DESKTOP_INITIAL_SESSIONS = 5;
const DESKTOP_SESSIONS_PER_PAGE = 5;
/** Sticky offset + section header — caps feed scroll area on md+ (Collapse uses height:auto). */
const DESKTOP_FEED_MAX_HEIGHT = 'calc(100vh - 120px - 3.5rem)';

/**
 * Recent Activity block — extracted verbatim from AnalyticsTwo.js.
 * Presentational; all state and handlers are passed from the page.
 */
const RecentActivitySection = ({
  recentActivityOpen,
  setRecentActivityOpen,
  isPartnerAdmin,
  feedRows,
  handleExportFeedCSV,
  feedFetchSettled,
  timeRange,
  partnerNameFilter,
  feedTotal,
  handleLoadMore,
  feedLoading,
  setActiveFeedRow,
  setActiveRowGalleryUrl,
  layoutRefByUuid,
  layoutUuidByMongoId,
}) => {
  const theme = useTheme();
  const isDesktopLayout = useMediaQuery(theme.breakpoints.up('md'));
  const initialSessions = isDesktopLayout ? DESKTOP_INITIAL_SESSIONS : MOBILE_INITIAL_SESSIONS;
  const sessionsPerPage = isDesktopLayout ? DESKTOP_SESSIONS_PER_PAGE : MOBILE_SESSIONS_PER_PAGE;

  return (
    <Box
      sx={{
        borderRadius: '14px',
        border: '1px solid rgba(0,0,0,0.06)',
        bgcolor: '#fff',
        mt: { xs: 0, sm: 0 },
        overflow: 'hidden',
        maxHeight: { md: 'calc(100vh - 120px)' },
      }}
    >
      <ButtonBase
        onClick={() => setRecentActivityOpen((v) => !v)}
        aria-expanded={recentActivityOpen}
        sx={{
          width: '100%',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 1.75, sm: 2 },
          py: { xs: 1.25, sm: 1.5 },
          borderRadius: '0 !important',
          textTransform: 'none !important',
          borderBottom: recentActivityOpen
            ? { md: '1px solid rgba(0,0,0,0.06)' }
            : undefined,
        }}
      >
        <Stack direction="column" spacing={0} sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
            <HistoryOutlined sx={{ fontSize: 15, color: 'rgba(0,0,0,0.4)', flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'rgba(0,0,0,0.5)', mr: 0.25 }}>
              Recent Activity
            </Typography>
            {!isPartnerAdmin && (
              <Box component="span" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                <MetricTooltip
                  {...METRIC_DEFINITIONS.recentActivity}
                  iconSize={13}
                  srLabel="About Recent Activity"
                />
              </Box>
            )}
          </Stack>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
          {feedRows.length > 0 && (
            <Box
              component="button"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleExportFeedCSV();
              }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'rgba(0,0,0,0.4)',
                fontSize: '0.78rem',
                fontWeight: 500,
                fontFamily: 'inherit',
                p: 0,
                lineHeight: 1,
                borderRadius: '0 !important',
                textTransform: 'none !important',
                '&:hover': { color: 'rgba(0,0,0,0.7)' },
              }}
            >
              <FileDownloadOutlined sx={{ fontSize: 15 }} />
              Export CSV
            </Box>
          )}
          <ExpandMore
            sx={{
              fontSize: 20,
              color: 'rgba(0,0,0,0.4)',
              transform: recentActivityOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 200ms ease',
            }}
          />
        </Stack>
      </ButtonBase>
      <Collapse in={recentActivityOpen} unmountOnExit>
        <Box
          sx={{
            overflowY: { md: 'auto' },
            overscrollBehavior: { md: 'contain' },
            maxHeight: { md: DESKTOP_FEED_MAX_HEIGHT },
          }}
        >
        {!feedFetchSettled ? (
          <Box sx={{ px: 2, pb: 2 }}>
            <Skeleton variant="rounded" height={120} sx={{ borderRadius: '12px', bgcolor: 'rgba(0,0,0,0.04)' }} />
            <Skeleton variant="rounded" height={120} sx={{ borderRadius: '12px', bgcolor: 'rgba(0,0,0,0.04)', mt: 1 }} />
          </Box>
        ) : (
          <FeedList
            key={`${timeRange}-${partnerNameFilter}`}
            rows={feedRows}
            loading={false}
            hasMore={feedRows.length < feedTotal}
            onLoadMore={handleLoadMore}
            loadingMore={feedLoading && feedRows.length > 0}
            onRowClick={(row, opts) => {
              setActiveFeedRow(row);
              setActiveRowGalleryUrl(opts?.galleryUrl || null);
            }}
            groupByRef={true}
            embedded={true}
            defaultExpandedFirst={isDesktopLayout}
            initialSessions={initialSessions}
            sessionsPerPage={sessionsPerPage}
            layoutRefByUuid={layoutRefByUuid}
            layoutUuidByMongoId={layoutUuidByMongoId}
          />
        )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default RecentActivitySection;
